import Link from 'next/link';
import { Accordion } from '@/components/shadcn/accordion';
import * as Typography from '@/components/shadcn/typography';
import { externalUrls } from '@/constants/urls';
import { faqItems } from '@/constants/privacy';
import { FAQItem } from '../ui/common/FAQs';

export default function Overview() {
  return (
    <div className="flex flex-col gap-12">
      <section className="flex flex-col gap-3">
        <Typography.H6>Summary of Changes</Typography.H6>
        <Typography.P className="text-neutral-300 md:text-sm">
          This Privacy Policy was updated on May 5, 2026 to reflect how the
          Hivemapper Network has evolved. In its early years, Devices were
          dashcams with limited compute, primarily capturing imagery for
          inclusion in a central map product that Hivemapper distributed to
          customers. As Device capabilities have grown &mdash; more compute,
          more sensors, more on-device intelligence &mdash; customers
          increasingly want to commission specific data and run their own models
          against Network coverage, rather than license pre-built data from
          Hivemapper. The Network has shifted to a marketplace in which
          Hivemapper operates the Devices and developers lease access through
          Work Orders, with data delivered directly from Devices to
          commissioning developers rather than to Hivemapper. This is simpler to
          operate and scales better. The transition is reflected in MIP-23 and
          MIP-26.
        </Typography.P>
        <Typography.P className="text-neutral-300 md:text-sm">
          Material changes to this Privacy Policy include: the Network is now a
          marketplace of sensors and compute; data collected by Devices flows
          directly to the developers who commission its production, not to
          Hivemapper; developers are required to comply with applicable privacy
          and data-protection laws as a condition of Network access; and
          Hivemapper facilitates (but does not execute) data removal requests on
          behalf of Contributors and Bystanders.
        </Typography.P>
      </section>

      <section className="flex flex-col gap-3">
        <Typography.H6>Overview</Typography.H6>
        <Typography.P className="text-neutral-300 md:text-sm">
          This Privacy Policy describes how Hivemapper, Inc.
          (&ldquo;Hivemapper,&rdquo; &ldquo;we,&rdquo; &ldquo;us,&rdquo; or
          &ldquo;our&rdquo;) handles personal information in connection with the
          Hivemapper Network (the &ldquo;Network&rdquo;), the Hivemapper
          website, the Hivemapper mobile applications, and Hivemapper-authorized
          devices (&ldquo;Devices&rdquo;). It applies to Contributors who
          operate Devices on the Network, visitors and users of the Hivemapper
          website and apps, and third parties (&ldquo;Bystanders&rdquo;) whose
          images or data may be incidentally captured by Devices operating on
          the Network. For the terms governing your participation in the Network
          as a Contributor, see the{' '}
          <Link
            className="cursor-pointer underline"
            href="/tos/mapping-network">
            Mapping Network Terms of Service
          </Link>
          .
        </Typography.P>
      </section>

      <section className="flex flex-col gap-3">
        <Typography.H6>How the Network Handles Data</Typography.H6>
        <Typography.P className="text-neutral-300 md:text-sm">
          The Hivemapper Network is a marketplace of sensors and compute.
          Developers post Work Orders asking the Network to collect data and run
          workloads in specific geographic areas. When a Contributor&apos;s
          Device fulfills a Work Order, the resulting data is delivered directly
          from the Device to the commissioning developer &mdash; not to
          Hivemapper. Hivemapper does not custody Contributor Data. Data from
          your Device does not sit on Hivemapper&apos;s servers; it flows
          through the Network protocol to the developer that paid for it.
          Developers receiving data through the Network are required to comply
          with applicable privacy and data-protection laws, and Hivemapper
          reserves the right to suspend or revoke a developer&apos;s Network
          access for violations.
        </Typography.P>
      </section>

      <section className="flex flex-col gap-3">
        <Typography.H6>What Is Collected</Typography.H6>
        <Typography.P className="text-neutral-300 md:text-sm">
          A Device on the Network may collect multiple data streams. Which
          streams a Device produces, and which are transmitted to a developer,
          depends on the Work Order that Device is fulfilling.
        </Typography.P>
        <div className="mt-4 grid grid-cols-2 gap-4 md:grid-cols-4">
          <div className="flex flex-col gap-2">
            <Typography.H6>Imagery &amp; Video</Typography.H6>
            <Typography.P className="text-neutral-300 md:text-sm">
              Street-level images and video frames from the Device&apos;s
              cameras.
            </Typography.P>
          </div>
          <div className="flex flex-col gap-2">
            <Typography.H6>Location (GNSS)</Typography.H6>
            <Typography.P className="text-neutral-300 md:text-sm">
              Geographic position of the Device.
            </Typography.P>
          </div>
          <div className="flex flex-col gap-2">
            <Typography.H6>Inertial (IMU)</Typography.H6>
            <Typography.P className="text-neutral-300 md:text-sm">
              Motion, acceleration, and orientation of the Device.
            </Typography.P>
          </div>
          <div className="flex flex-col gap-2">
            <Typography.H6>Derived Outputs</Typography.H6>
            <Typography.P className="text-neutral-300 md:text-sm">
              On-device computed outputs such as feature detections or
              workload-specific results.
            </Typography.P>
          </div>
        </div>
      </section>

      <section className="flex flex-col gap-3">
        <Typography.H6>How Your Information Is Used</Typography.H6>
        <div className="mt-2 flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <Typography.H6>As a Contributor</Typography.H6>
            <Typography.P className="text-neutral-300 md:text-sm">
              We collect account information (such as your wallet address and
              account identifiers) to enable participation and reward
              distribution. We collect metadata about your Device&apos;s
              activity (such as routes driven, hexes covered, Work Orders
              fulfilled) to operate the Network protocol, validate fulfillment,
              detect fraud, and distribute rewards. We do not custody the
              underlying sensor data your Device produces &mdash; that data
              flows directly to the developer who commissioned its collection.
            </Typography.P>
          </div>
          <div className="flex flex-col gap-2">
            <Typography.H6>As a Website or App User</Typography.H6>
            <Typography.P className="text-neutral-300 md:text-sm">
              If you visit the Hivemapper website or use the apps without
              operating a Device, we may collect standard usage information
              (such as IP address, browser type, pages viewed) to operate,
              maintain, and improve the site.
            </Typography.P>
          </div>
          <div className="flex flex-col gap-2">
            <Typography.H6>As a Bystander</Typography.H6>
            <Typography.P className="text-neutral-300 md:text-sm">
              If a Device operating on the Network captures your image or other
              personal information incidentally, see the Request Takedown
              section below.
            </Typography.P>
          </div>
        </div>
      </section>

      <section className="flex flex-col gap-3">
        <div className="mb-4">
          <Typography.H6>Privacy by Design</Typography.H6>
        </div>
        <Typography.H6>Randomly Generated Usernames</Typography.H6>
        <Typography.P className="text-neutral-300 md:text-sm">
          Each Contributor&rsquo;s identifier is randomly generated to support
          anonymity. Location data and collected data are not publicly tied to a
          real-world identity or Hivemapper profile.
        </Typography.P>
        <div className="mb-2 mt-4">
          <Typography.H6>Privacy Zones</Typography.H6>
        </div>
        <Typography.P className="text-neutral-300 md:text-sm">
          Contributors can configure personal privacy zones (geographic areas
          where their Device pauses collection). The Network also supports
          Network-level privacy zones where collection is paused for all
          Devices.
        </Typography.P>
        <div className="mb-2 mt-4">
          <Typography.H6>Developer Compliance Requirements</Typography.H6>
        </div>
        <Typography.P className="text-neutral-300 md:text-sm">
          Developers are required to comply with applicable laws regarding
          privacy and fair use of the data generated by Devices on the Network,
          including without limitation data-protection laws, export-control
          laws, and the rights of individuals captured in collected data.
          Hivemapper reserves the right to suspend or revoke a developer&apos;s
          access to the Network for violation of these obligations.
        </Typography.P>
        <div className="mb-2 mt-4">
          <Typography.H6>Transparency for Drivers</Typography.H6>
        </div>
        <Typography.P className="text-neutral-300 md:text-sm">
          In certain countries we require you to display a placard on your
          vehicle indicating that you are collecting for a map.{' '}
          <Link
            className="cursor-pointer underline"
            target="_blank"
            href={externalUrls.BeeLegalPlacards}>
            See here
          </Link>{' '}
          for the countries and placards.
        </Typography.P>
      </section>

      <section className="flex flex-col gap-3">
        <Typography.H6>Your Rights and Choices</Typography.H6>
        <Typography.P className="text-neutral-300 md:text-sm">
          You can view and update account information in the Apps. You may
          request account deletion through the Apps or by emailing{' '}
          <Link
            className="cursor-pointer underline"
            href="mailto:privacy@hivemapper.com">
            privacy@hivemapper.com
          </Link>
          . Account deletion removes your account information from
          Hivemapper&apos;s records. It does not affect Contributor Data already
          delivered to developers under Work Orders your Device fulfilled. To
          request removal, restriction, or access for Contributor Data your
          Device produced and that was delivered to a developer, contact
          Hivemapper. Hivemapper will facilitate the request with the receiving
          developer, but cannot guarantee the outcome &mdash; fulfillment
          depends on the developer&apos;s cooperation and any applicable legal
          retention requirements.
        </Typography.P>
      </section>

      <section className="flex flex-col gap-3">
        <Typography.H6>International Transfers</Typography.H6>
        <Typography.P className="text-neutral-300 md:text-sm">
          The Network operates globally. Contributor Data may be transmitted
          from a Device in one jurisdiction to a developer in another.
          Hivemapper&apos;s role in such transfers is limited to operating the
          protocol. Developers receiving data through the Network are
          responsible for ensuring lawful international transfer under
          applicable law.
        </Typography.P>
      </section>

      <section className="flex flex-col gap-3">
        <Accordion
          type="single"
          collapsible
          className="flex w-full flex-col gap-4">
          {faqItems.map(question => (
            <FAQItem
              key={question.title}
              question={question.title}
              answer={question.content}
            />
          ))}
        </Accordion>
      </section>

      <section className="flex flex-col gap-3">
        <Typography.H6>Request Takedown</Typography.H6>
        <Typography.P className="text-neutral-300 md:text-sm">
          If a Device operating on the Network captures your image, license
          plate, or other personal information and you would like to request
          removal, please{' '}
          <Link
            className="cursor-pointer underline"
            href="/privacy/redaction-request">
            complete this form
          </Link>{' '}
          or send a message to{' '}
          <Link
            className="cursor-pointer underline"
            href="mailto:privacy@hivemapper.com">
            privacy@hivemapper.com
          </Link>
          . Because Hivemapper does not custody Contributor Data, Hivemapper
          will facilitate your request with the developer(s) who may hold
          relevant data. Hivemapper cannot guarantee the outcome of any request,
          as fulfillment depends on the developer&apos;s cooperation and
          applicable legal retention requirements.
        </Typography.P>
      </section>

      <section className="flex flex-col gap-3">
        <Typography.H6>Active Data Collection</Typography.H6>
        <Typography.P className="text-neutral-300 md:text-sm">
          As of Q2 2026, data is being collected across the world. You can also
          view map coverage{' '}
          <Link className="cursor-pointer underline" href="/coverage">
            here.
          </Link>
        </Typography.P>
      </section>
    </div>
  );
}
